import { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { ArrowLeft, ArrowRight, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export interface WorkflowStep {
  key: string
  label: string
  icon: React.ComponentType<{ className?: string }>
}

interface WorkflowLayoutProps {
  children: ReactNode
  fileName: string
  currentStepIndex: number
  steps: WorkflowStep[]
  canGoBack: boolean
  canGoNext: boolean
  isProcessing: boolean
  onBack: () => void
  onNext: () => void
  nextButtonLabel?: string
  showCompleteButton?: boolean
  hideNavigation?: boolean
}

export function WorkflowLayout({
  children,
  fileName,
  currentStepIndex,
  steps,
  canGoBack,
  canGoNext,
  isProcessing,
  onBack,
  onNext,
  nextButtonLabel = 'Nästa',
  showCompleteButton = false,
  hideNavigation = false
}: WorkflowLayoutProps) {
  const navigate = useNavigate()
  const progress = ((currentStepIndex + 1) / steps.length) * 100

  return (
    <div className="space-y-6">

      {/* Progress Bar */}
      <div className="bg-white rounded-lg border p-4">
        <Progress value={progress} className="w-full h-2 mb-4" />

        {/* Step indicators */}
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const isActive = index === currentStepIndex
            const isCompleted = index < currentStepIndex
            const isLastStep = index === steps.length - 1
            const Icon = step.icon

            // Determine step indicator color
            function getStepColor(): 'green' | 'blue' | 'gray' {
              // Last step (Skapa Inkomster) should be green when active or completed
              if (isLastStep && (isActive || isCompleted)) return 'green'
              if (isActive) return 'blue'
              if (isCompleted) return 'green'
              return 'gray'
            }
            const color = getStepColor()

            return (
              <div
                key={step.key}
                className={`flex flex-col items-center gap-1 flex-1 ${
                  color === 'green' ? 'text-green-600' :
                  color === 'blue' ? 'text-blue-600' :
                  'text-gray-400'
                }`}
              >
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  color === 'green' ? 'border-green-600 bg-green-50' :
                  color === 'blue' ? 'border-blue-600 bg-blue-50' :
                  'border-gray-300 bg-gray-50'
                }`}>
                  <Icon className="h-5 w-5" />
                </div>
                <span className="text-xs font-medium text-center hidden sm:block">{step.label}</span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        {children}
      </div>

      {/* Footer Navigation */}
      {!hideNavigation && (
        <div className="py-4">
          {showCompleteButton ? (
            <div className="flex justify-end">
              <Button
                onClick={() => navigate('/upload')}
              >
                Slutför
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={onBack}
                disabled={!canGoBack || isProcessing}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Tillbaka
              </Button>

              <Button
                onClick={onNext}
                disabled={!canGoNext || isProcessing}
                className="flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    {nextButtonLabel}
                  </>
                ) : (
                  <>
                    {nextButtonLabel}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
